/**
 * BSMS Security Operations Platform - API Error Utilities
 * 
 * P0: Provides consistent error message extraction from API responses
 * to display real backend validation errors instead of generic messages.
 */

/**
 * Extract a user-friendly error message from an API error response.
 * 
 * Handles both old-style errors (detail as string) and new structured errors:
 * {
 *   error_code: "NOT_FOUND",
 *   message: "Officer not found",
 *   details: { entity_type: "officer", entity_id: "123" }
 * }
 * 
 * @param {Error} error - The error caught from an API call
 * @param {string} fallbackMessage - Default message if no specific error found
 * @returns {string} User-friendly error message
 */
export function extractApiError(error, fallbackMessage = 'An error occurred. Please try again.') {
  // Check for axios response
  const errorData = error?.response?.data;
  
  if (!errorData) {
  const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false;

  if (isOffline) {
    return 'You are currently offline. Reconnecting…';
  }

  return 'Connection interrupted. Retrying…';
}
  
  
  // Handle new structured error format
  if (errorData.detail && typeof errorData.detail === 'object') {
  const { message, error_code } = errorData.detail;
    
    if (message) {
      // Add context for specific error codes
      switch (error_code) {
        case 'NOT_FOUND':
          return `${message}. Please verify the selection exists.`;
        case 'VALIDATION_ERROR':
          return `Validation error: ${message}`;
        case 'OVERLAPPING_SHIFT':
          return `${message}. Please choose a different time or officer.`;
        case 'DUPLICATE':
          return `${message}. This record already exists.`;
        case 'INVALID_ROLE':
          return `Permission denied: ${message}`;
        case 'GEOFENCE_ERROR':
          return message; // Already has full context
        case 'INVALID_STATUS':
          return message; // Already descriptive
        default:
          return message;
      }
    }
  }
  
  // Handle old-style string detail
  if (typeof errorData.detail === 'string') {
    return errorData.detail;
  }
  
  // Handle message field directly
  if (errorData.message) {
    return errorData.message;
  }
  
  return fallbackMessage;
}

/**
 * Extract error code from API response
 * @param {Error} error - The error caught from an API call
 * @returns {string|null} Error code or null
 */
export function extractApiErrorCode(error) {
  const errorData = error?.response?.data?.detail;
  if (errorData && typeof errorData === 'object') {
    return errorData.error_code || null;
  }
  return null;
}

/**
 * Check if error is a specific type
 * @param {Error} error - The error caught from an API call
 * @param {string} errorCode - Error code to check for
 * @returns {boolean}
 */
export function isApiError(error, errorCode) {
  return extractApiErrorCode(error) === errorCode;
}

/**
 * Check if error is a 404 Not Found
 * @param {Error} error 
 * @returns {boolean}
 */
export function isNotFoundError(error) {
  return error?.response?.status === 404 || isApiError(error, 'NOT_FOUND');
}

/**
 * Check if error is a validation error
 * @param {Error} error 
 * @returns {boolean}
 */
export function isValidationError(error) {
  return error?.response?.status === 400 || isApiError(error, 'VALIDATION_ERROR');
}

/**
 * Check if error is an authentication error
 * @param {Error} error 
 * @returns {boolean}
 */
export function isAuthError(error) {
  return error?.response?.status === 401 || isApiError(error, 'UNAUTHORIZED');
}

/**
 * Check if error is a permission error
 * @param {Error} error 
 * @returns {boolean}
 */
export function isForbiddenError(error) {
  return error?.response?.status === 403 || isApiError(error, 'FORBIDDEN');
}
